import { mapBackendToFormFields, getFrontendSourceType, getFrontendCategory } from './citationFieldMapper';
import { generateInText } from './citationGenerator';
import { formatLocatorValue } from './citationLocatorHelper';
import { wrapCitationInsertHtml } from './citationDocumentMarkers';
import { buildDirectQuoteInsertHtml } from './citationQuoteUtils';
import { apaReferenceSortKey } from './referenceOutputUtils';

/**
 * Build wrapped citation HTML + insert metadata for persistence (re-sync after source edit).
 * Mirrors logic in DocumentEditor.handleInsertCitationSubmit.
 */
export function buildCitationInsertPayload(citation, insertData, priorOccurrenceInBody = 0) {
	const { style, type, quote: rawQuote, locator, locatorType, transitionVerb } = insertData;
	const quote = rawQuote ? rawQuote.trim() : '';

	const { subcategory, sourceType } = citation;
	const frontendCategory = getFrontendCategory(citation);
	const frontendSourceType = getFrontendSourceType(subcategory || sourceType, citation);
	const formFields = mapBackendToFormFields(citation);
	const inTextCitations = generateInText(frontendCategory, frontendSourceType, formFields, {
		isFirstOrganizationCite: priorOccurrenceInBody === 0
	});
	const formattedLocator = locator ? formatLocatorValue(locatorType, locator) : '';

	const citeId = citation._id || citation.id;

	let textToInsert = '';

	if (style === 'Paraphrase') {
		textToInsert =
			type === 'Parenthetical'
				? inTextCitations.parenthetical
				: `${inTextCitations.narrative}`;
	} else {
		textToInsert = buildDirectQuoteInsertHtml({
			quote,
			citationType: type,
			inTextCitations,
			formattedLocator,
			transitionVerb: transitionVerb || ''
		});
	}

	const meta = { style, type, quote, locator, locatorType, transitionVerb: transitionVerb || '' };
	const wrapped = wrapCitationInsertHtml(textToInsert, citeId, meta);
	return { html: wrapped, innerHtml: textToInsert, meta, citeId };
}

/**
 * Strip invisible / problematic characters often pasted from Word (reduces "black square" glyphs).
 */
export function stripInvisibleText(str) {
	if (!str || typeof str !== 'string') return str;
	return str
		.replace(/\u200b|\u200c|\u200d|\ufeff|\u00ad/g, '')
		.replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, '');
}

/**
 * Strip inline font-size / font-family / line-height (and Word mso-*) from pasted nodes so body matches editor defaults.
 */
export function stripPastedInlineFontStyles(root) {
	if (!root || root.nodeType !== 1) return;
	const walk = (el) => {
		if (el.nodeType !== 1) return;
		if (el.hasAttribute('style')) {
			let st = el.getAttribute('style') || '';
			st = st
				.replace(/font-family\s*:\s*[^;]+;?/gi, '')
				.replace(/font-size\s*:\s*[^;]+;?/gi, '')
				.replace(/line-height\s*:\s*[^;]+;?/gi, '')
				.replace(/mso-[^;]+;?/gi, '')
				.replace(/;\s*;/g, ';')
				.replace(/^\s*;\s*|\s*;\s*$/g, '')
				.trim();
			if (st) el.setAttribute('style', st);
			else el.removeAttribute('style');
		}
		for (const c of el.childNodes || []) {
			if (c.nodeType === 1) walk(c);
		}
	};
	walk(root);
}

/**
 * Alphabetize auto-generated reference paragraphs (stable citation ids).
 */
export function sortReferenceEntryParagraphs(editor, sectionBody) {
	if (!editor || !sectionBody?.querySelectorAll) return;
	const refs = Array.from(sectionBody.querySelectorAll('p.reference-entry[data-wf-reference-citation-id]'));
	if (refs.length <= 1) return;
	refs.sort((a, b) => {
		const keyA = apaReferenceSortKey(a.innerHTML || a.textContent);
		const keyB = apaReferenceSortKey(b.innerHTML || b.textContent);
		const cmp = keyA.localeCompare(keyB, 'en', { sensitivity: 'base' });
		if (cmp !== 0) return cmp;
		return (a.getAttribute('data-wf-reference-citation-id') || '').localeCompare(
			b.getAttribute('data-wf-reference-citation-id') || ''
		);
	});
	refs.forEach((p) => p.remove());
	refs.forEach((p) => sectionBody.appendChild(p));
}
